/**
 * Basemap raster layers for the MapLibre style.
 *
 * Two free, keyless Esri sources sit under the census-block vector layer:
 * World Street Map and World Imagery. OSM's public tiles are blocked in
 * browsers, and CARTO's former public CDN now watermarks "API key required".
 * Attribution is wired through the map's attribution control.
 *
 * `ensureBasemap(map, choice)` is idempotent: it adds the raster sources
 * once and toggles layer visibility on subsequent calls, so switching is
 * instant with no re-fetch of already-loaded tiles.
 */
import type maplibregl from 'maplibre-gl';
import type { BasemapChoice } from '../stores/basemap';

const STREETS_SOURCE_ID = 'basemap-streets';
const SATELLITE_SOURCE_ID = 'basemap-satellite';
const STREETS_LAYER_ID = 'basemap-streets-layer';
const SATELLITE_LAYER_ID = 'basemap-satellite-layer';

interface BasemapDef {
  sourceId: string;
  layerId: string;
  tiles: string[];
  attribution: string;
  maxzoom: number;
}

const BASEMAPS: Record<Exclude<BasemapChoice, 'none'>, BasemapDef> = {
  streets: {
    sourceId: STREETS_SOURCE_ID,
    layerId: STREETS_LAYER_ID,
    tiles: [
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
    ],
    attribution: 'Esri, HERE, Garmin, USGS',
    maxzoom: 19,
  },
  satellite: {
    sourceId: SATELLITE_SOURCE_ID,
    layerId: SATELLITE_LAYER_ID,
    tiles: [
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    ],
    attribution: 'Esri World Imagery',
    maxzoom: 19,
  },
};

/**
 * Add basemap sources/layers if missing, then show only the chosen one.
 * Must be called after the style has loaded. Safe to call repeatedly.
 */
export function ensureBasemap(map: maplibregl.Map, choice: BasemapChoice): void {
  for (const key of ['streets', 'satellite'] as const) {
    const def = BASEMAPS[key];
    if (!map.getSource(def.sourceId)) {
      map.addSource(def.sourceId, {
        type: 'raster',
        tiles: def.tiles,
        tileSize: 256,
        maxzoom: def.maxzoom,
        attribution: def.attribution,
      });
    }
    if (!map.getLayer(def.layerId)) {
      // Insert at the bottom of the stack, under the block layers.
      const firstLayer = map.getStyle().layers?.[0]?.id;
      map.addLayer(
        {
          id: def.layerId,
          type: 'raster',
          source: def.sourceId,
          paint: { 'raster-opacity': 1 },
        },
        firstLayer,
      );
    }
    map.setLayoutProperty(
      def.layerId,
      'visibility',
      choice === key ? 'visible' : 'none',
    );
  }
}
